import {
  type AgentTask,
  AgentTaskStatus,
} from "@/shared/domain/agentThread/AgentThreadRecord";

import { type MessageGroup } from "./groupToolMessages";

/**
 * Tool names that are pure task-list machinery and should not appear in the
 * transcript.
 *
 * These literals mirror the backend `get_name()` values of the task tools in
 * `packages/roboto_service/src/roboto_service/ai/tools/tasks.py`
 * (AddTasksTool / StartTaskTool / AddSubtasksTool / CompleteTaskTool) — there
 * is no shared cross-repo contract, so the set is pinned from both sides:
 * `taskSections.test.ts` pins this exact set, and `test_tasks.py` pins the
 * backend names against it. Renaming or adding a task tool requires updating
 * this set, and the backend pin test fails loudly if it is forgotten.
 */
export const HIDDEN_TASK_TOOL_NAMES: ReadonlySet<string> = new Set([
  "add_tasks",
  "start_task",
  "add_subtasks",
  "complete_task",
]);

/** A run of consecutive segments that belong to one concluded task, to be wrapped in a collapsible. */
export interface TaskBlock {
  kind: "task";
  taskId: number;
  title: string;
  segments: MessageGroup[];
}

/** A segment that belongs to no concluded task and renders inline. */
export interface LooseBlock {
  kind: "loose";
  segment: MessageGroup;
}

export type RenderBlock = TaskBlock | LooseBlock;

/**
 * A contiguous region of the conversation owned by one started task. A region
 * spans `[start, end]` inclusive: from the message that started the task through
 * the message whose tool call completed it (which carries the now-hidden
 * `complete_task` call). Messages after `end` — the closing summary, later user
 * turns — belong to no region and render loose, so a concluded task never
 * swallows the wrap-up that follows it.
 *
 * `end` is null for an in-progress task, whose region stays open to the end of
 * the thread; that region is never `concluded`, so it renders inline anyway and
 * live work stays visible.
 */
interface TaskRegion {
  taskId: number;
  title: string;
  concluded: boolean;
  start: number;
  /** Tie-breaker for tasks started in the same message (same `start`): the
   *  tool-call block index, so ordering and ownership stay deterministic. */
  startContent: number;
  /** Last message owned, inclusive; null while the task is still in progress. */
  end: number | null;
}

/**
 * Build the task regions for a thread, ordered by start position, from every
 * started task. Ties on the start message are broken by the tool-call block
 * index (`content_sequence_num`) so two tasks started in one assistant message
 * have a stable order.
 */
export function buildTaskRegions(
  tasks: AgentTask[] | null | undefined,
): TaskRegion[] {
  const regions: TaskRegion[] = [];
  for (const task of tasks ?? []) {
    // A started task (concluded or in-progress) has a start boundary; pending
    // tasks have none and own no conversation yet.
    if (task.start) {
      regions.push({
        taskId: task.task_id,
        title: task.title,
        concluded: task.status === AgentTaskStatus.COMPLETED,
        start: task.start.message_sequence_num,
        startContent: task.start.content_sequence_num,
        end: task.end?.message_sequence_num ?? null,
      });
    }
  }
  regions.sort((a, b) => a.start - b.start || a.startContent - b.startContent);
  return regions;
}

/**
 * The region that owns a message index: among regions whose span `[start, end]`
 * covers `index`, the last in sort order (greatest start, then greatest content).
 * Returns undefined for a message past every region's end — a loose trailing
 * turn that belongs to no task.
 */
function regionAt(
  regions: TaskRegion[],
  index: number,
): TaskRegion | undefined {
  let owner: TaskRegion | undefined;
  for (const region of regions) {
    // Regions are sorted by start; once a start is past index, so is the rest.
    if (region.start > index) {
      break;
    }
    if (region.end === null || index <= region.end) {
      owner = region;
    }
  }
  return owner;
}

/**
 * The id of the task whose region owns a message index, or undefined when the
 * message belongs to no started task (planning, trailing wrap-up). Used to tag
 * message articles with `data-task-id` so the task panel's scroll-spy and
 * click-to-jump can map between the conversation and the task list.
 */
export function taskIdForMessageIndex(
  regions: TaskRegion[],
  index: number,
): number | undefined {
  return regionAt(regions, index)?.taskId;
}

/** True when `index` is the first message of a different task region than `index - 1`. */
export function isTaskRegionBoundary(
  regions: TaskRegion[],
  index: number,
): boolean {
  if (regions.length === 0) {
    return false;
  }
  return (
    regionAt(regions, index)?.taskId !== regionAt(regions, index - 1)?.taskId
  );
}

/**
 * Group conversation segments into render blocks, wrapping the consecutive
 * segments of each concluded task so the view can render them inside one
 * collapsible. Each segment is assigned to the region owning its first message
 * index; because the segments were produced with the matching boundary predicate
 * (so none spans a region), the first index is representative of the whole
 * segment. Segments owned by no region, or by the active task, stay loose.
 */
export function groupSegmentsByConcludedTask(
  segments: MessageGroup[],
  regions: TaskRegion[],
): RenderBlock[] {
  if (regions.length === 0) {
    return segments.map((segment) => ({ kind: "loose", segment }));
  }

  const blocks: RenderBlock[] = [];
  let current: TaskBlock | null = null;

  const flush = () => {
    if (current) {
      blocks.push(current);
      current = null;
    }
  };

  for (const segment of segments) {
    const firstIndex = segment.messageIndices[0];
    const region =
      firstIndex === undefined ? undefined : regionAt(regions, firstIndex);

    if (!region || !region.concluded) {
      flush();
      blocks.push({ kind: "loose", segment });
      continue;
    }

    if (current && current.taskId === region.taskId) {
      current.segments.push(segment);
    } else {
      flush();
      current = {
        kind: "task",
        taskId: region.taskId,
        title: region.title,
        segments: [segment],
      };
    }
  }
  flush();

  return blocks;
}

/**
 * Whether a segment renders any visible content. A tool group made up entirely
 * of hidden task-machinery calls renders nothing (the view's `renderSegment`
 * filters those tool results out and returns null); a message segment always
 * carries at least one real message.
 */
function segmentHasVisibleContent(segment: MessageGroup): boolean {
  if (segment.type === "toolGroup") {
    return segment.toolResults.some(
      (toolResult) => !HIDDEN_TASK_TOOL_NAMES.has(toolResult.tool_name),
    );
  }
  return segment.messages.length > 0;
}

/**
 * Drop concluded-task blocks whose every segment is hidden task machinery.
 * Such a block (e.g. a task started and completed with no real work between)
 * would otherwise render a TaskSection header and chevron that expands to an
 * empty body. Loose blocks render inline with no wrapper, so an empty one
 * produces nothing visible and is left untouched.
 */
export function pruneEmptyTaskBlocks(blocks: RenderBlock[]): RenderBlock[] {
  return blocks.filter(
    (block) =>
      block.kind !== "task" || block.segments.some(segmentHasVisibleContent),
  );
}

/**
 * Find the concluded-task block that owns a given message, if any.
 *
 * A concluded task renders inside a collapsible whose body is unmounted while
 * the user has it closed, so a "scroll to message N" consumer (the admin thread
 * explorer) cannot find the target node when it lives in such a section. The
 * view uses this to auto-expand the owning section before the scroll fires.
 *
 * Returns the owning task id, or ``undefined`` when the message renders loose
 * (inline) or falls outside every concluded-task block.
 */
export function concludedTaskIdForMessage(
  blocks: RenderBlock[],
  messageSequenceNum: number,
): number | undefined {
  for (const block of blocks) {
    if (block.kind !== "task") {
      continue;
    }
    const owns = block.segments.some((segment) =>
      segment.messageIndices.includes(messageSequenceNum),
    );
    if (owns) {
      return block.taskId;
    }
  }
  return undefined;
}
